import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

interface SupportItem {
  platform: string;
  service: string;
  creditCost: number;
  status: string;
  supportsCache: boolean;
  supportsFreshCheck: boolean;
  publicMessage?: string | null;
}

interface EndpointItem {
  method: string;
  path: string;
  title: string;
  auth: string;
  description: string;
  body?: unknown;
  curl?: string;
  response?: unknown;
}

interface CacheBehavior {
  summary: string;
  flows: Array<{ case: string; http: number; status: string; result: string }>;
  lifecycle: string;
  cacheHitResponse?: unknown;
  cacheMissResponse?: unknown;
}

interface WorkerGuide {
  summary: string;
  authentication: string[];
  jobFlow: string[];
  leaseAndRetryRules: string[];
  workerResponsibilities: string[];
}

interface DocsData {
  title: string;
  version: string;
  baseUrl: string;
  description: string;
  currentSupport: SupportItem[];
  rules: string[];
  auth: {
    requiredHeaders: string[];
    optionalHeadersByKeyConfig: string[];
    keyTypes?: Record<string, string>;
  };
  statusMeaning: Array<{ status: string; meaning: string }>;
  endpoints: EndpointItem[];
  workerGuide?: WorkerGuide;
  workerEndpoints?: EndpointItem[];
  cacheBehavior?: CacheBehavior;
  managementPayload: unknown;
  errorCodes: string[];
  repoDoc: string;
}

const platformLabels: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X / Twitter',
  threads: 'Threads',
  telegram: 'Telegram'
};

const serviceLabels: Record<string, string> = {
  followers: 'Follower',
  following: '\u0110ang theo d\u00f5i',
  posts: 'B\u00e0i vi\u1ebft',
  likes: 'Like',
  views: 'View',
  shares: 'Share',
  comments: 'B\u00ecnh lu\u1eadn',
  saves: 'L\u01b0u',
  subscribers: 'Subscriber',
  reposts: 'Repost',
  members: 'Th\u00e0nh vi\u00ean'
};

const statusLabels: Record<string, string> = {
  active: '\u0110ang h\u1ed7 tr\u1ee3',
  degraded: 'K\u00e9m \u1ed5n \u0111\u1ecbnh',
  maintenance: 'B\u1ea3o tr\u00ec',
  disabled: 'Ch\u01b0a tri\u1ec3n khai',
  deprecated: 'S\u1eafp ng\u1eebng'
};

const formatPlatform = (value: string) => platformLabels[value] || value;
const formatService = (value: string) => serviceLabels[value] || value;
const formatStatus = (value: string) => statusLabels[value] || value;

const defaultSupport: SupportItem[] = [
  { platform: 'instagram', service: 'followers', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'instagram', service: 'following', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'instagram', service: 'posts', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'instagram', service: 'likes', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'instagram', service: 'comments', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'instagram', service: 'views', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'facebook', service: 'followers', creditCost: 2, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'facebook', service: 'likes', creditCost: 2, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'facebook', service: 'views', creditCost: 2, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'facebook', service: 'shares', creditCost: 2, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'facebook', service: 'comments', creditCost: 2, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'tiktok', service: 'followers', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'tiktok', service: 'likes', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'tiktok', service: 'views', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'tiktok', service: 'comments', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'tiktok', service: 'shares', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'tiktok', service: 'saves', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'youtube', service: 'subscribers', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'youtube', service: 'views', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'youtube', service: 'likes', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'youtube', service: 'comments', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'x', service: 'followers', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'x', service: 'likes', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'x', service: 'reposts', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'threads', service: 'followers', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'threads', service: 'likes', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false },
  { platform: 'telegram', service: 'members', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'telegram', service: 'subscribers', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true },
  { platform: 'telegram', service: 'views', creditCost: 1, status: 'disabled', supportsCache: false, supportsFreshCheck: false }
];

const defaultManagementPayload = {
  keyName: 'Customer A',
  rateLimit: -1,
  expiryDays: 0,
  isActive: true,
  platformLimits: [
    { platform: 'instagram', creditLimit: 1000, isEnabled: true, allowedServices: null },
    { platform: 'facebook', creditLimit: 1000, isEnabled: true, allowedServices: ['followers'] },
    { platform: 'tiktok', creditLimit: 1000, isEnabled: true, allowedServices: ['followers', 'likes', 'views', 'comments', 'shares', 'saves'] },
    { platform: 'telegram', creditLimit: 1000, isEnabled: true, allowedServices: ['members', 'subscribers'] }
  ]
};

const defaultCapabilitiesResponse = {
  success: true,
  data: [
    {
      platform: 'instagram',
      status: 'active',
      credit: { platform: 'instagram', enabled: true, limit: 1000, used: 12, remaining: 988 },
      services: [
        { service: 'followers', status: 'active', enabledForKey: true, creditCost: 1, supportsCache: true, supportsFreshCheck: true, message: null }
      ]
    }
  ]
};

const normalizeDocs = (raw: DocsData): DocsData => {
  const endpoints = (raw.endpoints || []).map(endpoint => ({
    ...endpoint,
    curl: endpoint.curl ? endpoint.curl.replace(/wa_your_api_key/g, 'sc_your_api_key') : endpoint.curl,
    response: endpoint.path === '/capabilities' ? defaultCapabilitiesResponse : endpoint.response
  }));
  const errorCodes = raw.errorCodes.includes('SERVICE_NOT_ENABLED')
    ? raw.errorCodes
    : [...raw.errorCodes, 'SERVICE_NOT_ENABLED'];

  return {
    ...raw,
    title: 'Check Count API Documentation',
    currentSupport: raw.currentSupport.length > 5 ? raw.currentSupport : defaultSupport,
    managementPayload: raw.managementPayload || defaultManagementPayload,
    endpoints,
    workerEndpoints: raw.workerEndpoints || [],
    errorCodes
  };
};

const markdownCodeBlock = (language: string, value: string) => [
  '```' + language,
  value,
  '```'
].join('\n');

const endpointToMarkdown = (endpoint: EndpointItem) => {
  const sections = [
    `## ${endpoint.method} ${endpoint.path}`,
    '',
    `**${endpoint.title}**`,
    '',
    `**Xác thực:** ${endpoint.auth}`,
    '',
    endpoint.description
  ];

  if (endpoint.body !== undefined && endpoint.body !== null) {
    sections.push('', '### Body', '', markdownCodeBlock('json', JSON.stringify(endpoint.body, null, 2)));
  }
  if (endpoint.curl) {
    sections.push('', '### cURL', '', markdownCodeBlock('bash', endpoint.curl));
  }
  if (endpoint.response !== undefined && endpoint.response !== null) {
    sections.push('', '### Response mẫu', '', markdownCodeBlock('json', JSON.stringify(endpoint.response, null, 2)));
  }

  return sections.join('\n');
};

const escapeTableCell = (value: string) => value.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');

const buildUserApiMarkdown = (docs: DocsData) => {
  const lines = [
    '# Check Count API — Tài liệu API người dùng',
    '',
    `Phiên bản: ${docs.version}`,
    '',
    `Base URL: ${docs.baseUrl}`,
    '',
    docs.description,
    '',
    '## Xác thực',
    '',
    `Header bắt buộc: ${docs.auth.requiredHeaders.map(value => '`' + value + '`').join(', ')}`,
    '',
    `Header tùy cấu hình key: ${docs.auth.optionalHeadersByKeyConfig.map(value => '`' + value + '`').join(', ')}`,
    '',
    'API người dùng yêu cầu Social Count key loại `user` hoặc `admin`. Worker key không được gọi các endpoint public.',
    '',
    '## Nền tảng và dịch vụ',
    '',
    '| Platform | Service | Trạng thái | Credit | Cache | Fresh check |',
    '| --- | --- | --- | ---: | --- | --- |',
    ...docs.currentSupport.map(item => `| ${escapeTableCell(item.platform)} | ${escapeTableCell(item.service)} | ${escapeTableCell(item.status)} | ${item.creditCost} | ${item.supportsCache ? 'Có' : 'Không'} | ${item.supportsFreshCheck ? 'Có' : 'Không'} |`),
    '',
    '## Quy tắc xử lý',
    '',
    ...docs.rules.map(rule => `- ${rule}`)
  ];

  if (docs.cacheBehavior) {
    lines.push('', '## Cache và fresh check', '', docs.cacheBehavior.summary, '');
    docs.cacheBehavior.flows.forEach(flow => {
      lines.push(`- **${flow.case}** — HTTP ${flow.http}, status \`${flow.status}\`: ${flow.result}`);
    });
    lines.push('', docs.cacheBehavior.lifecycle);
  }

  lines.push('', '# Endpoint API người dùng', '');
  docs.endpoints.forEach(endpoint => lines.push(endpointToMarkdown(endpoint), ''));
  lines.push('## Mã lỗi', '', docs.errorCodes.map(code => '`' + code + '`').join(', '), '');

  return lines.join('\n');
};

const buildWorkerApiMarkdown = (docs: DocsData) => {
  const workerBaseUrl = docs.baseUrl.replace(/\/api\/social-count\/?$/, '/api/social-count-management');
  const guide = docs.workerGuide;
  const lines = [
    '# Check Count API — Tài liệu Worker/Admin',
    '',
    `Phiên bản: ${docs.version}`,
    '',
    `Base URL: ${workerBaseUrl}`,
    '',
    '## Xác thực',
    '',
    '- Dùng Social Count API key loại `worker` hoặc `admin` qua header `X-API-Key`.',
    '- JWT nội bộ dùng quyền `social_count.read` hoặc `social_count.update` tùy endpoint.',
    '- Với worker key, backend lấy workerId từ `workerLabel` hoặc `keyName`; workerId trong query/body không ghi đè giá trị này.'
  ];

  if (guide) {
    lines.push('', '## Tổng quan', '', guide.summary);
    lines.push('', '## Luồng worker-only', '', ...guide.jobFlow.map((step, index) => `${index + 1}. ${step}`));
    lines.push('', '## Lease và retry', '', ...guide.leaseAndRetryRules.map(rule => `- ${rule}`));
    lines.push('', '## Trách nhiệm của worker', '', ...guide.workerResponsibilities.map(rule => `- ${rule}`));
  }

  lines.push('', '# Endpoint Worker/Admin', '');
  (docs.workerEndpoints || []).forEach(endpoint => lines.push(endpointToMarkdown(endpoint), ''));
  lines.push('## Mã lỗi', '', docs.errorCodes.map(code => '`' + code + '`').join(', '), '');

  return lines.join('\n');
};

const downloadMarkdown = (filename: string, content: string) => {
  const blob = new Blob(['\uFEFF', content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const SocialCountDocs: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const loadDocs = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch('/api/docs/social-count');
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 t\u1ea3i t\u00e0i li\u1ec7u API');
        }
        setDocs(normalizeDocs(data.data));
      } catch (err: any) {
        setError(err.message || 'Kh\u00f4ng th\u1ec3 t\u1ea3i t\u00e0i li\u1ec7u API');
      } finally {
        setLoading(false);
      }
    };

    loadDocs();
  }, []);

  const groupedSupport = useMemo(() => {
    const groups = new Map<string, SupportItem[]>();
    (docs?.currentSupport || []).forEach(item => {
      const list = groups.get(item.platform) || [];
      list.push(item);
      groups.set(item.platform, list);
    });
    return Array.from(groups.entries());
  }, [docs]);

  const copyToClipboard = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const jsonBlock = (value: unknown) => JSON.stringify(value, null, 2);

  if (loading) {
    return (
      <Layout currentPage="social-count">
        <div style={pageStyle}>{'\u0110ang t\u1ea3i t\u00e0i li\u1ec7u API...'}</div>
      </Layout>
    );
  }

  if (error || !docs) {
    return (
      <Layout currentPage="social-count">
        <div style={pageStyle}>
          <button style={secondaryButtonStyle} onClick={() => navigate('/social-count')}>{'Quay l\u1ea1i'}</button>
          <div style={errorStyle}>{error || 'Kh\u00f4ng c\u00f3 d\u1eef li\u1ec7u docs'}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="social-count">
      <div style={pageStyle}>
        <div style={headerStyle}>
          <div>
            <h1 style={titleStyle}>{'T\u00e0i li\u1ec7u Check Count API'}</h1>
            <p style={subtitleStyle}>{'Endpoint public cho kh\u00e1ch h\u00e0ng l\u1ea5y count theo n\u1ec1n t\u1ea3ng/d\u1ecbch v\u1ee5 v\u00e0 tr\u1eeb credit theo platform.'}</p>
            <div style={baseUrlStyle}>{docs.baseUrl}</div>
          </div>
          <div style={headerActionsStyle}>
            <button
              style={userDownloadButtonStyle}
              onClick={() => downloadMarkdown('check-count-user-api.md', buildUserApiMarkdown(docs))}
            >
              {'\u2b07 T\u1ea3i API ng\u01b0\u1eddi d\u00f9ng'}
            </button>
            <button
              style={workerDownloadButtonStyle}
              onClick={() => downloadMarkdown('check-count-worker-admin-api.md', buildWorkerApiMarkdown(docs))}
            >
              {'\u2b07 T\u1ea3i Worker/Admin API'}
            </button>
            <button style={secondaryButtonStyle} onClick={() => navigate('/social-count')}>{'Quay l\u1ea1i qu\u1ea3n l\u00fd key'}</button>
          </div>
        </div>

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'X\u00e1c th\u1ef1c'}</h2>
          <p style={textStyle}>{'M\u1ecdi public endpoint c\u1ea7n header API key. Device ID v\u00e0 Domain ch\u1ec9 c\u1ea7n g\u1eedi n\u1ebfu key \u0111\u01b0\u1ee3c c\u1ea5u h\u00ecnh b\u1ea3o v\u1ec7 th\u00eam.'}</p>
          <div style={pillRowStyle}>
            {docs.auth.requiredHeaders.map(header => <code key={header} style={pillStyle}>{header}</code>)}
            {docs.auth.optionalHeadersByKeyConfig.map(header => <code key={header} style={mutedPillStyle}>{header}</code>)}
          </div>
          {docs.auth.keyTypes && (
            <div style={guideGridStyle}>
              {Object.entries(docs.auth.keyTypes).map(([keyType, description]) => (
                <div key={keyType} style={guideCardStyle}>
                  <code style={keyTypeBadgeStyle(keyType)}>{keyType}</code>
                  <p style={{ ...textStyle, margin: '8px 0 0' }}>{description}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'N\u1ec1n t\u1ea3ng v\u00e0 d\u1ecbch v\u1ee5'}</h2>
          <div style={supportGridStyle}>
            {groupedSupport.map(([platform, services]) => (
              <div key={platform} style={supportCardStyle}>
                <h3 style={cardTitleStyle}>{formatPlatform(platform)}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {services.map(service => (
                    <span key={service.platform + '-' + service.service} style={serviceChipStyle(service.status)}>
                      {formatService(service.service)} - {service.creditCost} credit - {formatStatus(service.status)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'Quy t\u1eafc credit/cache'}</h2>
          <ul style={listStyle}>
            {docs.rules.map(rule => <li key={rule}>{rule}</li>)}
          </ul>
        </section>

        {docs.cacheBehavior && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Cache (cache=1 so với cache=0)</h2>
            <p style={textStyle}>{docs.cacheBehavior.summary}</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={cacheTableStyle}>
                <thead>
                  <tr>
                    <th style={cacheThStyle}>Trường hợp</th>
                    <th style={cacheThStyle}>HTTP</th>
                    <th style={cacheThStyle}>status</th>
                    <th style={cacheThStyle}>Kết quả</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.cacheBehavior.flows.map((flow, index) => (
                    <tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={cacheTdStyle}>{flow.case}</td>
                      <td style={cacheTdStyle}><code style={httpCodeStyle(flow.http)}>{flow.http}</code></td>
                      <td style={cacheTdStyle}><code>{flow.status}</code></td>
                      <td style={cacheTdStyle}>{flow.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ ...textStyle, marginTop: '14px' }}><strong>Vòng đời cache: </strong>{docs.cacheBehavior.lifecycle}</p>
            {docs.cacheBehavior.cacheHitResponse ? <CodeBlock title="Response — cache hit (200)" code={jsonBlock(docs.cacheBehavior.cacheHitResponse)} id="cache-hit" copied={copied} onCopy={copyToClipboard} /> : null}
            {docs.cacheBehavior.cacheMissResponse ? <CodeBlock title="Response — cache miss (404)" code={jsonBlock(docs.cacheBehavior.cacheMissResponse)} id="cache-miss" copied={copied} onCopy={copyToClipboard} /> : null}
          </section>
        )}

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'Endpoint public'}</h2>
          {docs.endpoints.map((endpoint, index) => (
            <div key={endpoint.method + '-' + endpoint.path} style={endpointStyle}>
              <div style={endpointHeaderStyle}>
                <span style={methodBadgeStyle(endpoint.method)}>{endpoint.method}</span>
                <code style={pathStyle}>{endpoint.path}</code>
              </div>
              <h3 style={endpointTitleStyle}>{endpoint.title}</h3>
              <p style={textStyle}>{endpoint.description}</p>
              {endpoint.body !== undefined && endpoint.body !== null && <CodeBlock title="Body" code={jsonBlock(endpoint.body)} id={'body-' + index} copied={copied} onCopy={copyToClipboard} />}
              {endpoint.curl ? <CodeBlock title="cURL" code={endpoint.curl} id={'curl-' + index} copied={copied} onCopy={copyToClipboard} /> : null}
              {endpoint.response !== undefined && endpoint.response !== null && <CodeBlock title="Response" code={jsonBlock(endpoint.response)} id={'response-' + index} copied={copied} onCopy={copyToClipboard} />}
            </div>
          ))}
        </section>

        {docs.workerGuide && (
          <section style={{ ...sectionStyle, borderColor: '#93c5fd', background: '#f8fbff' }}>
            <div style={workerSectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Worker API — xử lý Check Count</h2>
                <p style={textStyle}>{docs.workerGuide.summary}</p>
              </div>
              <span style={workerBadgeStyle}>worker / admin</span>
            </div>

            <div style={workerNoticeStyle}>
              <strong>Worker-only:</strong> backend chỉ tạo queue, không resolve link và không gọi provider. Phải có worker đang poll <code>/jobs/next</code>; nếu không request sẽ tiếp tục ở trạng thái <code>processing</code>.
            </div>

            <div style={guideGridStyle}>
              <GuideCard title="Xác thực" items={docs.workerGuide.authentication} />
              <GuideCard title="Lease và retry" items={docs.workerGuide.leaseAndRetryRules} />
              <GuideCard title="Trách nhiệm worker" items={docs.workerGuide.workerResponsibilities} />
            </div>

            <div style={flowStyle}>
              <h3 style={{ ...endpointTitleStyle, marginBottom: '12px' }}>Luồng worker-only đầy đủ</h3>
              <ol style={{ ...listStyle, margin: 0 }}>
                {docs.workerGuide.jobFlow.map(step => <li key={step}>{step}</li>)}
              </ol>
            </div>
          </section>
        )}

        {(docs.workerEndpoints || []).length > 0 && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Endpoint Worker/Admin</h2>
            <p style={textStyle}>Các đường dẫn dưới đây dùng base URL của backend. Worker nên giữ cùng một API key và worker identity trong suốt vòng đời xử lý job.</p>
            {(docs.workerEndpoints || []).map((endpoint, index) => (
              <div key={endpoint.method + '-' + endpoint.path} style={endpointStyle}>
                <div style={endpointHeaderStyle}>
                  <span style={methodBadgeStyle(endpoint.method)}>{endpoint.method}</span>
                  <code style={pathStyle}>{endpoint.path}</code>
                </div>
                <h3 style={endpointTitleStyle}>{endpoint.title}</h3>
                <div style={authLineStyle}><strong>Quyền:</strong> {endpoint.auth}</div>
                <p style={textStyle}>{endpoint.description}</p>
                {endpoint.body !== undefined && endpoint.body !== null && <CodeBlock title="Body" code={jsonBlock(endpoint.body)} id={'worker-body-' + index} copied={copied} onCopy={copyToClipboard} />}
                {endpoint.curl ? <CodeBlock title="cURL" code={endpoint.curl} id={'worker-curl-' + index} copied={copied} onCopy={copyToClipboard} /> : null}
                {endpoint.response !== undefined && endpoint.response !== null && <CodeBlock title="Response" code={jsonBlock(endpoint.response)} id={'worker-response-' + index} copied={copied} onCopy={copyToClipboard} />}
              </div>
            ))}
          </section>
        )}

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'Payload qu\u1ea3n l\u00fd key'}</h2>
          <p style={textStyle}>{'Trong trang qu\u1ea3n l\u00fd, platformLimits l\u00e0 danh s\u00e1ch n\u1ec1n t\u1ea3ng key \u0111\u01b0\u1ee3c ph\u00e9p d\u00f9ng. allowedServices=null ngh\u0129a l\u00e0 t\u1ea5t c\u1ea3 d\u1ecbch v\u1ee5 c\u1ee7a platform.'}</p>
          <CodeBlock title="Management Payload" code={jsonBlock(docs.managementPayload)} id="management-payload" copied={copied} onCopy={copyToClipboard} />
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{'M\u00e3 l\u1ed7i'}</h2>
          <div style={pillRowStyle}>
            {docs.errorCodes.map(code => <code key={code} style={mutedPillStyle}>{code}</code>)}
          </div>
        </section>
      </div>
    </Layout>
  );
};

interface CodeBlockProps {
  title: string;
  code: string;
  id: string;
  copied: string | null;
  onCopy: (text: string, id: string) => void;
}

const GuideCard: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div style={guideCardStyle}>
    <h3 style={{ ...endpointTitleStyle, marginBottom: '8px' }}>{title}</h3>
    <ul style={compactListStyle}>
      {items.map(item => <li key={item}>{item}</li>)}
    </ul>
  </div>
);

const CodeBlock: React.FC<CodeBlockProps> = ({ title, code, id, copied, onCopy }) => (
  <div style={codeBlockStyle}>
    <div style={codeHeaderStyle}>
      <span>{title}</span>
      <button style={copyButtonStyle} onClick={() => onCopy(code, id)}>{copied === id ? '\u0110\u00e3 copy' : 'Copy'}</button>
    </div>
    <pre style={preStyle}>{code}</pre>
  </div>
);

const pageStyle: React.CSSProperties = { padding: '28px', maxWidth: '1280px', margin: '0 auto', color: '#111827' };
const headerStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' };
const headerActionsStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px' };
const titleStyle: React.CSSProperties = { margin: '0 0 8px', fontSize: '30px', fontWeight: 800 };
const subtitleStyle: React.CSSProperties = { margin: '0 0 12px', color: '#64748b', fontSize: '15px', lineHeight: 1.6 };
const baseUrlStyle: React.CSSProperties = { display: 'inline-block', padding: '8px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px' };
const sectionStyle: React.CSSProperties = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '18px', marginBottom: '16px' };
const sectionTitleStyle: React.CSSProperties = { margin: '0 0 12px', fontSize: '20px', fontWeight: 800 };
const textStyle: React.CSSProperties = { color: '#475569', lineHeight: 1.6, margin: '0 0 12px' };
const pillRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '8px' };
const pillStyle: React.CSSProperties = { padding: '5px 8px', borderRadius: '6px', background: '#dbeafe', color: '#1e40af', fontSize: '12px' };
const mutedPillStyle: React.CSSProperties = { padding: '5px 8px', borderRadius: '6px', background: '#f1f5f9', color: '#475569', fontSize: '12px' };
const guideGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginTop: '14px' };
const guideCardStyle: React.CSSProperties = { border: '1px solid #dbeafe', borderRadius: '8px', padding: '14px', background: '#ffffff' };
const compactListStyle: React.CSSProperties = { margin: 0, paddingLeft: '18px', color: '#475569', lineHeight: 1.65, fontSize: '13px' };
const keyTypeBadgeStyle = (keyType: string): React.CSSProperties => ({ display: 'inline-block', padding: '4px 8px', borderRadius: '999px', background: keyType === 'admin' ? '#fef3c7' : keyType === 'worker' ? '#e0f2fe' : '#dcfce7', color: keyType === 'admin' ? '#92400e' : keyType === 'worker' ? '#075985' : '#166534', fontSize: '12px', fontWeight: 800 });
const supportGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' };
const supportCardStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', background: '#f8fafc' };
const cardTitleStyle: React.CSSProperties = { margin: '0 0 10px', fontSize: '16px', fontWeight: 800 };
const serviceChipStyle = (status: string): React.CSSProperties => ({ padding: '6px 8px', borderRadius: '999px', background: status === 'active' ? '#dcfce7' : '#fef3c7', color: status === 'active' ? '#166534' : '#92400e', fontSize: '12px', fontWeight: 700 });
const listStyle: React.CSSProperties = { margin: 0, paddingLeft: '20px', color: '#475569', lineHeight: 1.8 };
const cacheTableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const cacheThStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', background: '#f9fafb', whiteSpace: 'nowrap' };
const cacheTdStyle: React.CSSProperties = { padding: '10px', color: '#475569', verticalAlign: 'top', lineHeight: 1.5 };
const httpCodeStyle = (http: number): React.CSSProperties => ({ padding: '2px 6px', borderRadius: '6px', fontWeight: 700, background: http < 300 ? '#dcfce7' : http < 500 ? '#fef3c7' : '#fee2e2', color: http < 300 ? '#166534' : http < 500 ? '#92400e' : '#991b1b' });

const endpointStyle: React.CSSProperties = { borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '16px' };
const endpointHeaderStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' };
const methodBadgeStyle = (method: string): React.CSSProperties => ({ padding: '4px 8px', borderRadius: '6px', background: method === 'GET' ? '#dcfce7' : '#dbeafe', color: method === 'GET' ? '#166534' : '#1e40af', fontSize: '12px', fontWeight: 800 });
const pathStyle: React.CSSProperties = { fontSize: '13px', color: '#334155' };
const endpointTitleStyle: React.CSSProperties = { margin: '0 0 8px', fontSize: '17px' };
const authLineStyle: React.CSSProperties = { display: 'inline-block', marginBottom: '8px', padding: '5px 8px', borderRadius: '6px', background: '#eff6ff', color: '#1e40af', fontSize: '12px' };
const workerSectionHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' };
const workerBadgeStyle: React.CSSProperties = { flexShrink: 0, padding: '6px 10px', borderRadius: '999px', background: '#dbeafe', color: '#1e40af', fontSize: '12px', fontWeight: 800 };
const workerNoticeStyle: React.CSSProperties = { padding: '12px 14px', border: '1px solid #fbbf24', borderRadius: '8px', background: '#fffbeb', color: '#78350f', lineHeight: 1.6, fontSize: '14px' };
const flowStyle: React.CSSProperties = { marginTop: '14px', padding: '14px', border: '1px solid #bfdbfe', borderRadius: '8px', background: '#eff6ff' };
const codeBlockStyle: React.CSSProperties = { borderRadius: '8px', overflow: 'hidden', border: '1px solid #1e293b', marginTop: '10px', background: '#0f172a' };
const codeHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#1e293b', color: '#cbd5e1', fontSize: '12px', fontWeight: 800 };
const copyButtonStyle: React.CSSProperties = { border: '1px solid #475569', background: '#334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' };
const preStyle: React.CSSProperties = { margin: 0, padding: '14px', overflowX: 'auto', color: '#e2e8f0', fontSize: '13px', lineHeight: 1.55 };
const secondaryButtonStyle: React.CSSProperties = { padding: '9px 13px', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white', color: '#374151', cursor: 'pointer', fontWeight: 700 };
const userDownloadButtonStyle: React.CSSProperties = { padding: '9px 13px', border: '1px solid #16a34a', borderRadius: '8px', background: '#16a34a', color: '#ffffff', cursor: 'pointer', fontWeight: 700 };
const workerDownloadButtonStyle: React.CSSProperties = { padding: '9px 13px', border: '1px solid #2563eb', borderRadius: '8px', background: '#2563eb', color: '#ffffff', cursor: 'pointer', fontWeight: 700 };
const errorStyle: React.CSSProperties = { marginTop: '16px', padding: '14px', borderRadius: '8px', background: '#fee2e2', color: '#991b1b' };

export default SocialCountDocs;
